import download from 'downloadjs';
import queryString from 'query-string';
import configs from 'configs';
import { invoke } from 'functions/api/invokeRequest';
import { getUserCountry } from 'modules/user/utils';
import analytics from 'functions/analytics';
import { authHeaders } from 'functions/authHeader';
import request from 'functions/api/request';
import { formatMatchesResultFromApi } from 'functions/formatMatchesResultFromApi';

const apiUrl = configs.beApiUrl;

export const getMatches = async (data) => {
  const {
    patientId, lat, lng, distance, lang, matchFlow, newest,
  } = data;
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/profile/matches`,
    qs: {
      patientId,
      lat,
      lng,
      distance,
      lang,
      matchFlow,
      newest,
    },
    skipNull: true,
    headers: await authHeaders(),
    json: true,
  };

  try {
    response = await request.get(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while getting trials matches:', ex);
    throw ex;
  }

  return formatMatchesResultFromApi(response);
};

export const getTrialByNCT = async ({
  nctNumber, usersCountry, conditions = {
    isMobile: false,
    isForPDF: false,
    lang: 'en',
  },
}) => {
  const conditionsParam = {
    isMobile: false,
    isForPDF: false,
    lang: 'en',
    ...conditions,
  };
  let response = null;
  let url = `${apiUrl}/public/trial/findByNctNumber/${encodeURIComponent(nctNumber)}`;
  let queryParams = {
    conditions: JSON.stringify(conditionsParam),
  };
  if (usersCountry) {
    const { lat, lng } = usersCountry;
    queryParams = {
      ...queryParams,
      lat,
      lng,
    };
  }
  url = `${url}?${queryString.stringify(queryParams)}`;

  const requestOptions = {
    url,
    headers: await authHeaders(),
    json: true,
  };
  try {
    response = await request.get(requestOptions);
  } catch (ex) {
    console.error('[getTrialByNCT]: Error occurred, while getting trial:', ex);
    throw ex;
  }
  if (!response?.success || !response?.data?.nct) {
    throw new Error('Bad response from the server');
  }
  return response.data;
};

export const contactTrialForMoreInfo = (payload) => invoke('/trial/contact/trialTeamForInfo', null, payload);

export const downloadSponsorPDF = (nctNumber, queryParams) => {
  const { conditions, ...restQueryParams } = queryParams;
  return invoke('/profile/pdf/sponsor', {
    ...restQueryParams,
    conditions: JSON.stringify(conditions),
  })
    .then((data) => download(data, `Sponsor - ${nctNumber}.pdf`, 'application/pdf'))
    .catch((error) => {
      console.error(error);
      return false;
    });
};

export const getFormattedProfile = (pdf, lang, setLink = false) => invoke('/profile/details', {
  format: pdf ? 0 : 1,
  pdf: pdf ? 1 : 0,
  lang,
  setLink,
});

export const sendDoctorEmail = (values, params) => new Promise((resolve) => {
  invoke('/user/profile', {
    lang: params?.lang || null,
  }).then((result) => {
    const country = getUserCountry(result) || {};
    const lat = (country.location && country.location.lat) || null;
    const lng = (country.location && country.location.lng) || null;

    const body = {
      firstName: values.doctorFirstName,
      lastName: values.doctorLastName,
      email: values.doctorEmail || '',
      clinicName: values.clinicName || '',
      subjectLine: values.subjectLine || '',
      msg: values.msg || '',
      info: result.info,
      trialsUrl: `${configs.beApiUrl}/profile/pdf/matches?${queryString.stringify({
        profileId: result.personal.profile_id,
        providerCode: result.personal.providerCode,
        lat,
        lng,
        country: country.name || null,
      }, { skipNull: true })}`,
    };
    return invoke('/profile/trials', { email: 1 }, body).then(() => {
      resolve();
    });
  });
});

export const explainFnFp = (data) => invoke('/user/explain/fnfp', {}, data);

export const downloadTrialsPDF = ({
  cancerType, providerCode, pdfFor = null,
}) => {
  analytics.downloadPdfFromPopup();

  const body = {
    cancerType,
    providerCode,
    pdfFor, // actually it is expected to be an array
  };

  return invoke('/profile/trials', { pdf: 1 }, body)
    .then((data) => {
      download(data, 'Treatment Match List.pdf', 'application/pdf');
    })
    .catch((error) => {
      console.error(error);
      throw error;
    });
};

export const downloadProfilePDF = (lang) => getFormattedProfile(true, lang)
  .then((data) => {
    download(data, 'Medical_profile.pdf', 'application/pdf');
  })
  .catch((error) => console.error(error));
